import { useEffect, useState } from "react";
import { Bot, Loader2, PhoneCall, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const RULES = [
  "Only ever quotes prices that are on the customer's quote — never estimates, rounds or discounts",
  "Never approves, declines or books work — sends the customer to their quote link and hands over",
  "Never diagnoses new faults; it can explain why a quoted item matters",
  "Hands over to a person for bookings, price talk, finance, complaints or anything it isn't sure about",
  "Introduces itself as your AI assistant on its first reply, and stays quiet if a colleague replied in the last 10 minutes",
];

// Settings → "AI assistant" tab. Per-workshop switch (default OFF) plus the
// assistant's name and the holding line customers get when it hands over.
export const AiAdvisorPane = ({ conf, saving, onSave }) => {
  const [name, setName] = useState("");
  const [handoff, setHandoff] = useState("");

  useEffect(() => {
    if (!conf) return;
    setName(conf.assistant_name || "");
    setHandoff(conf.handoff_message || "");
  }, [conf]);

  if (!conf) {
    return <div className="p-10 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  const dirty = name !== (conf.assistant_name || "") || handoff !== (conf.handoff_message || "");

  return (
    <div className="p-6 space-y-6" data-testid="settings-ai-pane">
      <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
            <Bot className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="label-overline text-primary">AI service assistant</div>
              <button
                type="button"
                onClick={() => onSave({ enabled: !conf.enabled })}
                disabled={saving || !conf.configured}
                role="switch"
                aria-checked={!!conf.enabled}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${conf.enabled ? "bg-primary" : "bg-muted"} ${!conf.configured ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="settings-ai-advisor-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${conf.enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              When on, customer texts and WhatsApps about their quote (&ldquo;what does the brake pad thing mean?&rdquo;, &ldquo;is it ready yet?&rdquo;) get answered within seconds using the real items and prices on that job — so the desk still runs when someone&apos;s off sick or at lunch. Every reply lands in the job&apos;s message thread, and you can pause it on any single customer to take over.
            </p>
            <p className="text-[11px] font-semibold mt-2" data-testid="settings-ai-advisor-state">
              {conf.enabled ? <span className="text-success">ON — answering customers now</span> : <span className="text-muted-foreground">OFF — every message waits for a person</span>}
            </p>
            {!conf.configured && (
              <p className="text-[11px] text-amber-200/90 mt-2">AI key not configured on the platform yet.</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-success" />
          <div className="label-overline">What it will never do</div>
        </div>
        <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground leading-relaxed" data-testid="settings-ai-advisor-rules">
          {RULES.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </div>

      <div>
        <label htmlFor="ai-name" className="label-overline">Assistant name (shown to your team)</label>
        <input
          id="ai-name" type="text" value={name} maxLength={40}
          onChange={(e) => setName(e.target.value)}
          className="input-default text-base mt-2"
          data-testid="settings-ai-advisor-name"
        />
      </div>

      <div>
        <label htmlFor="ai-handoff" className="label-overline">Holding line when it hands over to a person</label>
        <textarea
          id="ai-handoff" rows={2} value={handoff} maxLength={300}
          onChange={(e) => setHandoff(e.target.value)}
          className="input-default font-mono-plex text-sm leading-relaxed mt-2"
          data-testid="settings-ai-advisor-handoff"
        />
        <p className="text-[11px] text-muted-foreground mt-1">Placeholders: <span className="font-mono-plex">{"{first_name}"}</span> <span className="font-mono-plex">{"{advisor}"}</span></p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onSave({ assistant_name: name, handoff_message: handoff })}
          disabled={saving || !dirty}
          className="btn-primary"
          data-testid="settings-ai-advisor-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save assistant settings
        </button>
      </div>

      <PhoneReceptionistCard />
    </div>
  );
};

// Phone receptionist — answers the workshop line, reads the caller's live car
// status, and transfers / takes a message. Separate toggle from the text assistant.
const PhoneReceptionistCard = () => {
  const [conf, setConf] = useState(null);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/workshop/phone-receptionist").then((r) => { setConf(r.data); setNumber(r.data.transfer_number || ""); }).catch(() => {});
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch("/workshop/phone-receptionist", patch);
      setConf(r.data);
      setNumber(r.data.transfer_number || "");
      toast.success(patch.enabled === undefined ? "Desk number saved" : r.data.enabled ? "Phone receptionist is ON" : "Phone receptionist off — calls go straight to the desk number");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the phone receptionist");
    } finally { setBusy(false); }
  };

  if (!conf) return null;
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.04] p-4 mt-2" data-testid="settings-phone-receptionist">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center shrink-0">
          <PhoneCall className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="label-overline text-primary">Phone receptionist</div>
            <button
              type="button" role="switch" aria-checked={!!conf.enabled}
              onClick={() => save({ enabled: !conf.enabled })}
              disabled={busy}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${conf.enabled ? "bg-primary" : "bg-muted"}`}
              data-testid="settings-phone-receptionist-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${conf.enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            When a customer rings {conf.inbound_number ? <span className="font-mono-plex text-foreground">{conf.inbound_number}</span> : "the workshop line"}, the assistant recognises their mobile, tells them where their car is and what it&apos;s promised for, and offers: <strong>1</strong> speak to the team (rings the desk number below), <strong>2</strong> hear it again, <strong>3</strong> leave a message — which lands in the job&apos;s thread with a play button. When off, calls ring the desk number directly.
          </p>
          <div className="mt-3 flex items-end gap-3">
            <div className="flex-1">
              <label htmlFor="desk-number" className="label-overline">Desk number for transfers</label>
              <input
                id="desk-number" type="tel" value={number} onChange={(e) => setNumber(e.target.value)}
                placeholder="e.g. 01484 123456"
                className="input-default font-mono-plex text-base mt-2"
                data-testid="settings-phone-receptionist-number"
              />
            </div>
            <button type="button" onClick={() => save({ transfer_number: number })} disabled={busy || number === (conf.transfer_number || "")} className="btn-primary" data-testid="settings-phone-receptionist-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 font-mono-plex break-all">
            Twilio → Phone Numbers → Voice → &ldquo;A call comes in&rdquo;: {conf.webhook_url}
          </p>
        </div>
      </div>
    </div>
  );
};
